/**
 * Create a role, or edit one (routes P16 create, P17 update).
 *
 * **The permission checkboxes are built by iterating the `PERMISSIONS`
 * registry from `@rd/shared` — never from string literals** (criterion 13).
 * A key renamed in the contract re-renders this form correctly by
 * construction; a hand-written list would silently keep offering the old
 * spelling. Specifically it iterates `TENANT_PERMISSION_KEYS`: the API
 * refuses PLATFORM-scope keys on an account role (`FORBIDDEN_PERMISSION`,
 * `roles.service.ts`), and the reserved keys (§4.3 — bulk orders, whitelist,
 * KYC) are excluded from that export precisely because the upstream 403s
 * them, so a checkbox for one would be a control that can only fail.
 *
 * `key` is **create-only**: the PATCH schema has no `key` field — a role's
 * key is its address (roles have no ref prefix, §7), and renaming an address
 * is a delete-and-recreate, not an edit. On edit it renders as static text.
 */

import {
  PERMISSIONS,
  TENANT_PERMISSION_KEYS,
  type PermissionKey,
  type RoleDto,
  type UpdateRoleRequest,
} from '@rd/shared';
import { useEffect, useMemo, useState, type ReactElement } from 'react';

import { Button } from '../../components/Button.js';
import { ErrorState } from '../../components/ErrorState.js';
import { Field } from '../../components/Field.js';
import { Input, Checkbox } from '../../components/Input.js';
import { Modal } from '../../components/Modal.js';
import { ApiClientError } from '../../lib/apiClient.js';

import { useCreateRole, useUpdateRole } from './useRoles.js';

/**
 * The checkboxes, grouped by the key's domain segment (`sims.read` → "sims")
 * so forty-two boxes read as a handful of families rather than a wall. The
 * grouping is derived from the keys themselves — a new domain in the catalog
 * grows a new group here with no edit.
 */
function groupedPermissions(): { group: string; keys: PermissionKey[] }[] {
  const byGroup = new Map<string, PermissionKey[]>();
  for (const key of TENANT_PERMISSION_KEYS) {
    const group = key.split('.')[0] ?? key;
    const bucket = byGroup.get(group) ?? [];
    bucket.push(key);
    byGroup.set(group, bucket);
  }
  return [...byGroup.entries()].map(([group, keys]) => ({ group, keys }));
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v) => b.includes(v));
}

export function RoleFormModal({
  open,
  role,
  onClose,
}: {
  open: boolean;
  /** `null` creates a role; a row edits that role. Never a system role. */
  role: RoleDto | null;
  onClose(): void;
}): ReactElement | null {
  const create = useCreateRole();
  const update = useUpdateRole();

  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  /** The ticked keys. Named for what it means, not for the DTO field. */
  const [granted, setGranted] = useState<PermissionKey[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const groups = useMemo(groupedPermissions, []);

  // Re-seed on every open, so a closed-and-reopened form never shows the
  // previous subject's half-edited state.
  useEffect(() => {
    if (!open) return;
    setKey(role?.key ?? '');
    setName(role?.name ?? '');
    setDescription(role?.description ?? '');
    setGranted(role ? [...role.permissions] : []);
    setFieldErrors({});
    setFormError(null);
  }, [open, role]);

  if (!open) return null;

  const busy = create.isPending || update.isPending;

  const toggle = (permissionKey: PermissionKey): void => {
    setGranted((current) =>
      current.includes(permissionKey)
        ? current.filter((k) => k !== permissionKey)
        : [...current, permissionKey],
    );
  };

  const submit = async (): Promise<void> => {
    setFieldErrors({});
    setFormError(null);
    try {
      if (role === null) {
        await create.mutateAsync({
          key: key.trim(),
          name: name.trim(),
          // Optional in the schema; an empty box is absence, not `""`.
          ...(description.trim() === '' ? {} : { description: description.trim() }),
          permissions: granted,
        });
        onClose();
        return;
      }

      // Changed fields only — the strict PATCH schema wants at least one, so
      // an untouched form closes without a request.
      const changes: UpdateRoleRequest = {
        ...(name.trim() !== role.name ? { name: name.trim() } : {}),
        ...(description.trim() !== (role.description ?? '')
          ? { description: description.trim() }
          : {}),
        ...(sameSet(granted, role.permissions) ? {} : { permissions: granted }),
      };
      if (Object.keys(changes).length === 0) {
        onClose();
        return;
      }
      await update.mutateAsync({ roleKey: role.key, changes });
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError) {
        const bound = err.fieldErrors();
        setFieldErrors(bound);
        if (Object.keys(bound).length === 0) setFormError(err.message);
      } else {
        setFormError('The panel could not reach the server. Try again.');
      }
    }
  };

  return (
    <Modal
      open={open}
      title={role === null ? 'Create a role' : `Edit ${role.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {role === null ? 'Create role' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-[var(--space-4)]"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {/* `ErrorState`, not a hand-tinted paragraph — the danger colour stays
            in the primitives that own error presentation (criterion 25). */}
        {formError !== null ? <ErrorState title="That did not save" body={formError} /> : null}

        <Field
          label="Key"
          required
          hint="Upper snake case, e.g. FLEET_OPERATOR. Cannot be changed later."
          error={fieldErrors['key']}
        >
          {(field) =>
            role === null ? (
              <Input
                {...field}
                value={key}
                // The server's own pattern (`roles.schema.ts`), declared here
                // so the browser flags it before the round-trip does.
                pattern="[A-Z][A-Z0-9_]*"
                className="font-[family-name:var(--font-mono)]"
                onChange={(event) => setKey(event.target.value)}
              />
            ) : (
              <p className="font-[family-name:var(--font-mono)] text-[length:var(--text-sm)] text-muted">
                {role.key}
              </p>
            )
          }
        </Field>

        <Field label="Name" required error={fieldErrors['name']}>
          {(field) => (
            <Input {...field} value={name} onChange={(event) => setName(event.target.value)} />
          )}
        </Field>

        <Field label="Description" error={fieldErrors['description']}>
          {(field) => (
            <Input
              {...field}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Permissions"
          required
          hint="What anyone holding this role may do."
          error={fieldErrors['permissions']}
        >
          {() => (
            <div className="flex flex-col gap-[var(--space-3)]">
              {groups.map(({ group, keys }) => (
                <fieldset key={group} className="flex flex-col gap-[var(--space-1)]">
                  <legend className="mb-1 text-[length:var(--text-2xs)] font-medium uppercase tracking-[var(--tracking-caps)] text-muted">
                    {group}
                  </legend>
                  {keys.map((permissionKey) => (
                    <label
                      key={permissionKey}
                      className="flex items-center gap-[var(--space-2)] text-[length:var(--text-sm)] text-body"
                    >
                      <Checkbox
                        checked={granted.includes(permissionKey)}
                        onChange={() => toggle(permissionKey)}
                      />
                      {/* The registry's own label — the same wording the
                          superadmin console uses for the same key. */}
                      {PERMISSIONS[permissionKey].label}
                    </label>
                  ))}
                </fieldset>
              ))}
            </div>
          )}
        </Field>
      </form>
    </Modal>
  );
}
